import { useCallback } from 'react';
import { getPrimaryOriginalUrl } from '@/utils/canvasCropping';
import { getAvailableQuartersFromPage, getScreenshotDataForQuarterFromPage } from '../utils/companyDataUtils';

export const useEditorHandlers = (
  editorState: any,
  annotationState: any,
  maskState: any,
  cropState: any,
  asset: any,
  pageData: any,
  companyLoading: boolean,
  toast: any,
  isSecondaryDraggingRef: React.RefObject<boolean>,
  secondaryDragStartRef: React.RefObject<{ x: number; y: number }>
) => {
  // Handle aspect ratio change
  const handleAspectRatioChange = useCallback((ratio: string) => {
    editorState.setSelectedAspectRatio(ratio);
    cropState.setCropMode(false);
    editorState.setCropCoordinates((prev: any) => {
      const { innerCrop, ...rest } = prev;
      return rest;
    });
  }, [editorState, cropState]);

  // Handle comparison mode change
  const handleComparisonModeChange = useCallback((mode: string) => {
    editorState.setComparisonMode(mode);
    
    if (mode !== 'single' && !editorState.secondaryImageData && pageData) {
      const availableQuarters = getAvailableQuartersFromPage(pageData);
      const currentPrimaryVersion = editorState.primaryImageData?.version || asset.version;
      
      const currentIndex = availableQuarters.findIndex((q: any) => q.version === currentPrimaryVersion);
      const nextQuarterData = availableQuarters[currentIndex + 1] || availableQuarters[0];
      
      if (nextQuarterData && nextQuarterData.version !== currentPrimaryVersion) {
        handleVersionSelection(nextQuarterData.version, nextQuarterData.quarter, 'secondary');
      }
    }
    
    if (mode === 'single') {
      editorState.setSecondaryImageData(null);
    }
  }, [editorState, pageData, asset]);

  // Handle tool changes
  const handleToolChange = useCallback((tool: string) => {
    if (tool === 'hand') {
      annotationState.setIsAnnotationMode(false);
      maskState.setIsMaskMode(false);
      annotationState.setCurrentAnnotation(null);
      annotationState.setIsDrawingAnnotation(false);
      annotationState.setSelectedAnnotationId(null);
      maskState.setCurrentMask(null);
      maskState.setIsDrawingMask(false);
      maskState.setSelectedMaskId(null);
    } else if (tool === 'mask') {
      maskState.setIsMaskMode(true);
      annotationState.setIsAnnotationMode(false);
      cropState.setCropMode(false);
      annotationState.setCurrentAnnotation(null);
      annotationState.setIsDrawingAnnotation(false);
      annotationState.setSelectedAnnotationId(null);
      maskState.setCurrentMask(null);
      maskState.setIsDrawingMask(false);
    }
  }, [annotationState, maskState, cropState]);

  // Handle zoom operations
  const handleZoom = useCallback((direction: 'in' | 'out', target?: 'primary' | 'secondary') => {
    const delta = direction === 'in' ? 0.2 : -0.2;
    
    if (target === 'secondary') {
      // Secondary image zoom
      const newScale = Math.min(Math.max(editorState.secondaryZoomCoordinates.scale + delta, 0.5), 4.0);
      
      editorState.setSecondaryZoomCoordinates((prev: any) => ({
        ...prev,
        scale: newScale
      }));
      
      // Sync primary image if linked
      if (editorState.isZoomPanLinked) {
        editorState.setZoomCoordinates((prev: any) => ({
          ...prev,
          scale: newScale
        }));
      }
    } else {
      // Primary image zoom (default)
      const newScale = Math.min(Math.max(editorState.zoomCoordinates.scale + delta, 0.5), 4.0);
      
      editorState.setZoomCoordinates((prev: any) => ({
        ...prev,
        scale: newScale
      }));
      
      // Sync secondary image if in dual mode and linked
      if (editorState.comparisonMode !== 'single' && editorState.isZoomPanLinked) {
        editorState.setSecondaryZoomCoordinates((prev: any) => ({
          ...prev,
          scale: newScale
        }));
      }
    }
  }, [editorState]);

  // Handle zoom/pan link toggle
  const handleZoomPanLinkToggle = useCallback(() => {
    editorState.setIsZoomPanLinked(!editorState.isZoomPanLinked);
    
    // When linking, sync secondary to primary
    if (!editorState.isZoomPanLinked && editorState.comparisonMode !== 'single') {
      editorState.setSecondaryZoomCoordinates({
        ...editorState.zoomCoordinates,
        viewportWidth: editorState.secondaryZoomCoordinates.viewportWidth,
        viewportHeight: editorState.secondaryZoomCoordinates.viewportHeight
      });
    }
  }, [editorState]);

  const handleReset = useCallback(() => {
    const originalImageUrl = getPrimaryOriginalUrl(asset);
    editorState.setPrimaryImageData({
      version: asset.version,
      pageId: asset.page_id,
      imageUrl: originalImageUrl || asset.primary_image
    });
    
    if (editorState.secondaryImageData) {
      editorState.setSecondaryImageData({
        version: editorState.secondaryImageData.version,
        pageId: editorState.secondaryImageData.pageId,
        imageUrl: asset.secondary_image
      });
    }
    
    const resetCoordinates = (prev: any) => ({
      ...prev,
      x: 0,
      y: 0,
      scale: 1,
      innerCrop: undefined,
      viewportWidth: prev.viewportWidth,
      viewportHeight: prev.viewportHeight
    });
    
    editorState.setZoomCoordinates(resetCoordinates);
    editorState.setCropCoordinates(resetCoordinates);
    
    if (editorState.comparisonMode !== 'single') {
      editorState.setSecondaryZoomCoordinates(resetCoordinates);
      editorState.setSecondaryCropCoordinates(resetCoordinates);
    }
    
    editorState.setTempCrop(null);
    editorState.setTempSecondaryCrop(null);
    editorState.tempCropRef.current = null;
    editorState.tempSecondaryCropRef.current = null;
    
    // Reset header and subheader to original asset values (not empty)
    editorState.setHeader(asset.header || '');
    editorState.setSubheader(asset.subheader || '');

    // Clear all annotations - use requestAnimationFrame for React 18 compatibility
    requestAnimationFrame(() => {
      annotationState.clearAllAnnotations();
    });

    localStorage.removeItem(`canva_design_${asset.id}_primary`);
    localStorage.removeItem(`canva_design_${asset.id}_secondary`);
    localStorage.removeItem(`canva_design_${asset.id}`);

    toast({
      title: "Reset Complete",
      description: "Image reset to original. Next Canva edit will create a fresh design.",
    });
  }, [editorState, asset, toast]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!annotationState.isAnnotationMode && !maskState.isMaskMode) {
      editorState.isDraggingRef.current = true;
      editorState.dragStartRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [editorState, annotationState, maskState]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (editorState.isDraggingRef.current && !annotationState.isAnnotationMode && !maskState.isMaskMode) {
      const deltaX = e.clientX - editorState.dragStartRef.current.x;
      const deltaY = e.clientY - editorState.dragStartRef.current.y;

      editorState.setZoomCoordinates((prev: any) => ({
        ...prev,
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));

      // Sync secondary image panning if in dual mode and linked
      if (editorState.comparisonMode !== 'single' && editorState.isZoomPanLinked) {
        editorState.setSecondaryZoomCoordinates((prev: any) => ({
          ...prev,
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));
      }

      editorState.dragStartRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [editorState, annotationState, maskState]);

  const handleMouseUp = useCallback(() => {
    editorState.isDraggingRef.current = false;
  }, [editorState]);

  // Secondary panel mouse handlers
  const handleSecondaryMouseDown = useCallback((e: React.MouseEvent) => {
    if (!annotationState.isAnnotationMode && !maskState.isMaskMode) {
      isSecondaryDraggingRef.current = true;
      secondaryDragStartRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [annotationState, maskState, isSecondaryDraggingRef, secondaryDragStartRef]);

  const handleSecondaryMouseMove = useCallback((e: React.MouseEvent) => {
    if (isSecondaryDraggingRef.current && !annotationState.isAnnotationMode && !maskState.isMaskMode) {
      const deltaX = e.clientX - secondaryDragStartRef.current!.x;
      const deltaY = e.clientY - secondaryDragStartRef.current!.y;

      editorState.setSecondaryZoomCoordinates((prev: any) => {
        const newCoords = {
          ...prev,
          x: prev.x + deltaX,
          y: prev.y + deltaY
        };
        return newCoords;
      });

      // Sync primary image panning if linked
      if (editorState.isZoomPanLinked) {
        editorState.setZoomCoordinates((prev: any) => ({
          ...prev,
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));
      }

      secondaryDragStartRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [editorState, annotationState, maskState, isSecondaryDraggingRef, secondaryDragStartRef]);

  const handleSecondaryMouseUp = useCallback(() => {
    if (isSecondaryDraggingRef.current) {
      isSecondaryDraggingRef.current = false;
    }
  }, [isSecondaryDraggingRef]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    
    const zoomSpeed = 0.002;
    const delta = -e.deltaY * zoomSpeed;
    const newScale = Math.min(Math.max(editorState.zoomCoordinates.scale + delta, 0.5), 4.0);
    
    editorState.setZoomCoordinates((prev: any) => ({
      ...prev,
      scale: newScale
    }));
    
    // Sync secondary image if in dual mode and linked
    if (editorState.comparisonMode !== 'single' && editorState.isZoomPanLinked) {
      editorState.setSecondaryZoomCoordinates((prev: any) => ({
        ...prev,
        scale: newScale
      }));
    }
  }, [editorState]);

  const handleSecondaryWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    
    const zoomSpeed = 0.002;
    const delta = -e.deltaY * zoomSpeed;
    const newScale = Math.min(Math.max(editorState.secondaryZoomCoordinates.scale + delta, 0.5), 4.0);
    
    editorState.setSecondaryZoomCoordinates((prev: any) => ({
      ...prev,
      scale: newScale
    }));
    
    // Sync primary image if linked
    if (editorState.isZoomPanLinked) {
      editorState.setZoomCoordinates((prev: any) => ({
        ...prev,
        scale: newScale
      }));
    }
  }, [editorState]);

  // Version selection handler
  const handleVersionSelection = useCallback(async (version: string, description: string, target: 'primary' | 'secondary') => {
    if (companyLoading) {
      toast({
        title: "Loading...",
        description: "Company data is still loading",
        variant: "default",
      });
      return;
    }

    try {
      const availableQuarters = getAvailableQuartersFromPage(pageData!);
      const quarterData = availableQuarters.find((q: any) => q.version === version);
      
      if (!quarterData) {
        toast({
          title: "Version not found",
          description: `No data found for version ${version}`,
          variant: "destructive",
        });
        return;
      }

      const screenshotData = getScreenshotDataForQuarterFromPage(pageData!, quarterData.quarter);
      
      if (!screenshotData || !screenshotData.screenshotUrl) {
        toast({
          title: "No screenshot available",
          description: `No screenshot found for ${description}`,
          variant: "destructive",
        });
        return;
      }

      const newImageData = {
        version,
        pageId: asset.page_id,
        imageUrl: screenshotData.screenshotUrl
      };
      
      if (target === 'primary') {
        editorState.setPrimaryImageData(newImageData);
        
        // Store primary version in localStorage
        const storageKey = `asset_${asset.id}_primary_version`;
        localStorage.setItem(storageKey, version);
      } else {
        editorState.setSecondaryImageData(newImageData);
        
        // Store secondary version in localStorage
        const storageKey = `asset_${asset.id}_secondary_version`;
        localStorage.setItem(storageKey, version);
      }

      toast({
        title: "Version changed",
        description: `Switched ${target} view to ${description} (zoom/pan preserved)`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to switch version",
        variant: "destructive",
      });
    }
  }, [pageData, companyLoading, asset, editorState, toast]);

  // Handle primary image reset (remove markup and return to crop mode)
  const handlePrimaryReset = useCallback(async () => {
    const currentMode = editorState.primaryImageMode;
    
    // Only allow reset when in markup mode and markup URL exists
    if (currentMode !== 'markup' || !asset.primary_markup_url) {
      return;
    }
    
    const confirmed = window.confirm(
      "🔄 This will permanently remove your Canva-edited version and return to crop mode. " +
      "Your original image will remain, but the markup will be lost. Are you sure?"
    );
    
    if (!confirmed) {
      return;
    }
    
    
    // Mark that we want to reset (clear markup URL when saving)
    editorState.primaryResetRequested.current = true;
    
    // Get current version and quarter info
    const currentVersion = editorState.primaryImageData?.version || asset.version;
    const availableQuarters = getAvailableQuartersFromPage(pageData!);
    const currentQuarter = availableQuarters.find((q: any) => q.version === currentVersion);
    
    if (currentQuarter) {
      // Debug logging
      
      // Reset is equivalent to reselecting the same version - this handles everything
      await handleVersionSelection(currentQuarter.version, currentQuarter.quarter, 'primary');
      
      // Switch to crop mode after reselecting
      editorState.setPrimaryImageMode('crop');
      
      // Reset zoom/pan to defaults
      const resetCoordinates = {
        ...editorState.primaryCropCoordinates,
        x: 0,
        y: 0,
        scale: 1
      };
      
      editorState.setZoomCoordinates(resetCoordinates);
      editorState.setPrimaryCropCoordinates(resetCoordinates);
      
      toast({
        title: "Reset Complete",
        description: "Markup removed - you're back in crop mode with the original image",
      });
    } else {
      const availableQuarters = getAvailableQuartersFromPage(pageData!);
      const availableVersions = availableQuarters.map(q => q.version).join(', ');
      toast({
        title: "Reset Failed",
        description: `Version ${currentVersion} not found. Available versions: ${availableVersions || 'none'}`,
        variant: "destructive",
      });
    }
  }, [editorState, toast, asset, pageData, handleVersionSelection]);

  // Handle secondary image reset (remove markup and return to crop mode)
  const handleSecondaryReset = useCallback(async () => {
    const currentMode = editorState.secondaryImageMode;
    
    // Only allow reset when in markup mode and markup URL exists
    if (currentMode !== 'markup' || !asset.secondary_markup_url) {
      return;
    }
    
    const confirmed = window.confirm(
      "🔄 This will permanently remove your secondary Canva-edited version and return to crop mode. " +
      "Your original image will remain, but the markup will be lost. Are you sure?"
    );
    
    if (!confirmed) {
      return;
    }
    
    
    // Mark that we want to reset (clear markup URL when saving)
    editorState.secondaryResetRequested.current = true;
    
    // Get current version and quarter info
    let currentVersion = editorState.secondaryImageData?.version || asset.secondary_version;
    
    // If no secondary version is set, fall back to primary version or asset version
    if (!currentVersion) {
      currentVersion = editorState.primaryImageData?.version || asset.version;
    }
    
    if (currentVersion && pageData?.scrapes) {
      const availableQuarters = getAvailableQuartersFromPage(pageData!);
      let currentQuarter = availableQuarters.find((q: any) => q.version === currentVersion);
      
      // Debug logging
      
      // If we can't find the exact version, try to use the first available quarter as fallback
      if (!currentQuarter && availableQuarters.length > 0) {
        currentQuarter = availableQuarters[0];
      }
      
      if (currentQuarter) {
        // Reset is equivalent to reselecting the version - this handles everything
        await handleVersionSelection(currentQuarter.version, currentQuarter.quarter, 'secondary');
        
        // Switch to crop mode after reselecting
        editorState.setSecondaryImageMode('crop');
        
        // Reset zoom/pan to defaults
        const resetCoordinates = {
          ...editorState.secondaryCropModeCoordinates,
          x: 0,
          y: 0,
          scale: 1
        };
        
        editorState.setSecondaryZoomCoordinates(resetCoordinates);
        editorState.setSecondaryCropModeCoordinates(resetCoordinates);
        
        toast({
          title: "Reset Complete (Secondary)",
          description: "Markup removed - you're back in crop mode with the original image",
        });
      } else {
        const availableVersions = availableQuarters.map(q => q.version).join(', ');
        toast({
          title: "Reset Failed (Secondary)",
          description: `Version ${currentVersion} not found. Available versions: ${availableVersions || 'none'}`,
          variant: "destructive",
        });
      }
    } else {
      const availableQuarters = getAvailableQuartersFromPage(pageData || {});
      toast({
        title: "Reset Failed (Secondary)",
        description: `No page data or scrapes. Available quarters: ${availableQuarters.length}`,
        variant: "destructive",
      });
    }
  }, [editorState, toast, asset, pageData, handleVersionSelection]);

  return {
    handleAspectRatioChange,
    handleComparisonModeChange,
    handleToolChange,
    handleZoom,
    handleZoomPanLinkToggle,
    handleReset,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleSecondaryMouseDown,
    handleSecondaryMouseMove,
    handleSecondaryMouseUp,
    handleWheel,
    handleSecondaryWheel,
    handleVersionSelection,
    handlePrimaryReset,
    handleSecondaryReset
  };
};